/**
 * Aurora — 首次启动引导向导（用户选择需要的模型）
 * 3 步：欢迎 → 选择模型 → 完成
 */
import { useState, useEffect } from 'react';
import { AuroraLogo } from './Icons';
import './OnboardingWizard.css';

interface Model {
  id: string;
  name: string;
  description: string;
  supplier: string;
  category: 'text' | 'image' | 'video' | 'audio';
  vision?: boolean;
  reasoning?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  text: '文本对话',
  image: '图片生成',
  video: '视频生成',
  audio: '语音合成',
};

const CATEGORY_ICONS: Record<string, string> = {
  text: '💬',
  image: '🎨',
  video: '🎬',
  audio: '🎙️',
};

const CATEGORIES = ['text', 'image', 'video', 'audio'] as const;

// 每个供应商的推荐选默认模型 ID
const RECOMMENDED_DEFAULTS = new Set([
  'qwen3.6-flash',
  'qwen3.7-plus',
  'deepseek-v4-flash-0731',
  'kimi-k2.6',
  'claude-sonnet-4-6',
  'gemini-2.5-flash',
  'NanoBanana2',
  'Wan2.7-image',
  'veo3.1_fast',
  'Wan2.7-video',
  'wuyin-tts',
  'wanx2.1-t2i-turbo',
]);

const STEPS = ['欢迎', '选择模型', '完成'];

interface Props {
  userId: string;
  onFinish: (selectedModels: string[]) => void;
}

export default function OnboardingWizard({ userId, onFinish }: Props) {
  const [step, setStep] = useState(0);
  const [models, setModels] = useState<Model[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(RECOMMENDED_DEFAULTS));
  const [activeCategory, setActiveCategory] = useState<typeof CATEGORIES[number]>('text');
  const [saving, setSaving] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    if (step === 1 && models.length === 0) {
      setLoadingModels(true);
      fetch('/api/models')
        .then((r) => r.json())
        .then((data) => {
          const list: Model[] = (data.models || data.data || []).filter((m: Model) => m.category);
          setModels(list);
          // 默认选中推荐模型（如果在列表里）
          const valid = new Set(list.map((m) => m.id));
          setSelected(new Set([...RECOMMENDED_DEFAULTS].filter((id) => valid.has(id))));
        })
        .catch(() => {})
        .finally(() => setLoadingModels(false));
    }
  }, [step]);

  const toggleModel = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const catIds = models.filter((m) => m.category === activeCategory).map((m) => m.id);
    setSelected((prev) => {
      const next = new Set(prev);
      catIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearAll = () => {
    const catIds = new Set(models.filter((m) => m.category === activeCategory).map((m) => m.id));
    setSelected((prev) => new Set([...prev].filter((id) => !catIds.has(id))));
  };

  const handleFinish = async () => {
    setSaving(true);
    const modelIds = [...selected];
    try {
      const token = localStorage.getItem('aurora_token');
      await fetch('/api/user/preferences', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ selectedModels: modelIds }),
      });
    } catch {}
    setSaving(false);
    setStep(2);
  };

  const catModels = models.filter((m) => m.category === activeCategory);
  const bySupplier = catModels.reduce<Record<string, Model[]>>((acc, m) => {
    (acc[m.supplier] ??= []).push(m);
    return acc;
  }, {});

  const selectedInCat = catModels.filter((m) => selected.has(m.id)).length;
  const progress = (step / (STEPS.length - 1)) * 100;

  return (
    <div className="ow__overlay">
      <div className="ow__card ow__card--wide">
        {/* 进度条 */}
        <div className="ow__progress-bar">
          <div className="ow__progress-fill" style={{ width: `${progress}%` }} />
        </div>

        {/* 步骤指示 */}
        <div className="ow__steps">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`ow__step-dot ${i === step ? 'ow__step-dot--active' : ''} ${i < step ? 'ow__step-dot--done' : ''}`}
            >
              <span>{i < step ? '✓' : i + 1}</span>
              <label>{s}</label>
            </div>
          ))}
        </div>

        {/* 步骤内容 */}
        <div className="ow__body">

          {/* 步骤 0：欢迎 */}
          {step === 0 && (
            <div className="ow__welcome">
              <div className="ow__logo"><AuroraLogo size={64} /></div>
              <h1 className="ow__title">欢迎使用 Aurora</h1>
              <p className="ow__subtitle">
                Aurora 是一款极光主题 AI IDE，支持多模型协同、图片/视频/语音生成。<br />
                所有 AI 服务由系统统一提供，你只需选择想用的模型即可。
              </p>
              <div className="ow__feature-list">
                <div className="ow__feature"><span>💬</span><span>多模型 AI 对话（千问、DeepSeek、Claude、GPT…）</span></div>
                <div className="ow__feature"><span>🎨</span><span>AI 图片生成（万相、NanoBanana、GPT-Image…）</span></div>
                <div className="ow__feature"><span>🎬</span><span>AI 视频生成（Vidu、可灵、Veo、MiniMax…）</span></div>
                <div className="ow__feature"><span>🎙️</span><span>语音合成 · 克隆 · 数字人</span></div>
              </div>
              <div className="ow__points-notice">
                <span className="ow__points-badge">🎁 注册赠 10,000 积分</span>
                <span className="ow__points-desc">1 积分 ≈ 0.01 元，可用于调用各类 AI 服务</span>
              </div>
            </div>
          )}

          {/* 步骤 1：模型选择 */}
          {step === 1 && (
            <div className="ow__model-picker">
              <h2 className="ow__title">选择你需要的模型</h2>
              <p className="ow__subtitle">已选 {selected.size} 个模型 · 后续可在「模型中心」随时调整</p>

              {/* 分类标签 */}
              <div className="ow__cat-tabs">
                {CATEGORIES.map((cat) => {
                  const cnt = models.filter((m) => m.category === cat && selected.has(m.id)).length;
                  const total = models.filter((m) => m.category === cat).length;
                  return (
                    <button
                      key={cat}
                      className={`ow__cat-tab ${activeCategory === cat ? 'ow__cat-tab--active' : ''}`}
                      onClick={() => setActiveCategory(cat)}
                    >
                      {CATEGORY_ICONS[cat]} {CATEGORY_LABELS[cat]}
                      {total > 0 && <span className="ow__cat-count">{cnt}/{total}</span>}
                    </button>
                  );
                })}
              </div>

              {/* 快捷操作 */}
              <div className="ow__cat-actions">
                <button className="ow__action-btn" onClick={selectAll}>全选</button>
                <button className="ow__action-btn" onClick={clearAll}>清空</button>
              </div>

              {/* 模型列表 */}
              <div className="ow__model-list">
                {loadingModels ? (
                  <div className="ow__loading">加载模型中…</div>
                ) : catModels.length === 0 ? (
                  <div className="ow__loading">暂无模型</div>
                ) : (
                  Object.entries(bySupplier).map(([supplier, mods]) => (
                    <div key={supplier} className="ow__supplier-group">
                      <div className="ow__supplier-label">{supplier}</div>
                      <div className="ow__model-grid">
                        {mods.map((m) => (
                          <button
                            key={m.id}
                            className={`ow__model-card ${selected.has(m.id) ? 'ow__model-card--selected' : ''}`}
                            onClick={() => toggleModel(m.id)}
                          >
                            <div className="ow__model-check">{selected.has(m.id) ? '✓' : ''}</div>
                            <div className="ow__model-name">{m.name}</div>
                            <div className="ow__model-desc">{m.description}</div>
                            <div className="ow__model-tags">
                              {m.vision && <span className="ow__tag">视觉</span>}
                              {m.reasoning && <span className="ow__tag">推理</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* 步骤 2：完成 */}
          {step === 2 && (
            <div className="ow__done">
              <div className="ow__done-icon">🎉</div>
              <h2 className="ow__title">配置完成！</h2>
              <p className="ow__subtitle">
                已选 {selected.size} 个模型，Aurora 已准备就绪。
              </p>
              <p className="ow__subtitle">你可以在「模型中心」随时调整模型选择。</p>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="ow__footer">
          {step > 0 && step < STEPS.length - 1 && (
            <button className="ow__btn ow__btn--ghost" onClick={() => setStep((s) => s - 1)}>
              返回
            </button>
          )}
          {step === 1 && (
            <button
              className="ow__btn ow__btn--primary"
              disabled={selected.size === 0 || saving}
              onClick={handleFinish}
            >
              {saving ? '保存中…' : '保存并继续 →'}
            </button>
          )}
          {step !== 1 && (
            <button
              className="ow__btn ow__btn--primary"
              onClick={() => {
                if (step === STEPS.length - 1) {
                  onFinish([...selected]);
                } else {
                  setStep((s) => s + 1);
                }
              }}
            >
              {step === STEPS.length - 1 ? '开始使用 →' : '下一步 →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
